// src/views/ProductsView.js
// Catálogo de Productos (SKU reutilizables, reorder-ready). PLAN-INTAKE-PRODUCTOS Fase A.
// CRUD calcado de MaterialsView; usa ProductModal + helpers de src/lib/products.
import React, { useState, useEffect, useMemo } from "react";
import { collection, getDocs, doc, deleteDoc, getDoc, setDoc } from "firebase/firestore";
import ProductModal from "../components/ProductModal";
import { createProduct, updateProduct, productQrPayload } from "../lib/products";
import { resolveCategories, getCategory } from "../lib/productCategories";
import { downloadQrPng } from "../utils/qrLabel";

const IconPlus = () => <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"></path></svg>;
const IconPencil = () => <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path d="M17.414 2.586a2 2 0 00-2.828 0L7 10.172V13h2.828l7.586-7.586a2 2 0 000-2.828z"></path><path fillRule="evenodd" d="M2 6a2 2 0 012-2h4a1 1 0 010 2H4v10h10v-4a1 1 0 112 0v4a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" clipRule="evenodd"></path></svg>;
const IconTrash = () => <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm4 0a1 1 0 012 0v6a1 1 0 11-2 0V8z" clipRule="evenodd"></path></svg>;
const IconQr = () => <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path d="M3 3h6v6H3V3zm2 2v2h2V5H5zM11 3h6v6h-6V3zm2 2v2h2V5h-2zM3 11h6v6H3v-6zm2 2v2h2v-2H5zM13 11h2v2h-2v-2zM15 13h2v2h-2v-2zM11 15h2v2h-2v-2zM15 15h2v2h-2v-2z"></path></svg>;
const IconEye = () => <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z"></path><circle cx="12" cy="12" r="3"></circle></svg>;

const ProductsView = ({ db, user, storage }) => {
  const [products, setProducts] = useState([]);
  const [clients, setClients] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState({ search: "", category: "all", kind: "all" });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [viewMode, setViewMode] = useState(false); // "Ver" = abrir el modal en solo lectura
  const [lightbox, setLightbox] = useState(null); // { url, title } para ver el 1UP en grande

  const canManage = user.permissions?.canManageMaterials; // catálogo: mismo permiso que materiales (MVP)

  // Cerrar el lightbox con Esc.
  useEffect(() => {
    if (!lightbox) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setLightbox(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox]);

  const fetchAll = async () => {
    setIsLoading(true);
    try {
      const [prodSnap, cliSnap, matSnap, cfgSnap] = await Promise.all([
        getDocs(collection(db, "products")),
        getDocs(collection(db, "clients")),
        getDocs(collection(db, "materials")),
        getDoc(doc(db, "project_settings", "productCategories")),
      ]);
      setProducts(prodSnap.docs.map((d) => ({ id: d.id, ...d.data() })).sort((a, b) => (a.name || "").localeCompare(b.name || "")));
      setClients(cliSnap.docs.map((d) => ({ id: d.id, ...d.data() })).sort((a, b) => (a.name || "").localeCompare(b.name || "")));
      setMaterials(matSnap.docs.map((d) => ({ id: d.id, ...d.data() })).sort((a, b) => (a.name || "").localeCompare(b.name || "")));
      setCategories(resolveCategories(cfgSnap.exists() ? cfgSnap.data().categories : null));
    } catch (e) {
      console.error("Error cargando catálogo de productos:", e);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, [db]);

  const clientName = (id) => clients.find((c) => c.id === id)?.name || "—";
  const catName = (id) => getCategory(categories, id)?.name || id || "—";

  const filtered = useMemo(() => {
    const s = filters.search.toLowerCase().trim();
    return products.filter((p) => {
      const matchS = !s
        || (p.name || "").toLowerCase().includes(s)
        || (p.sku || "").toLowerCase().includes(s)
        || (p.code || "").toLowerCase().includes(s);
      const matchC = filters.category === "all" || p.categoryId === filters.category;
      const matchK = filters.kind === "all" || (p.kind || "producto") === filters.kind;
      return matchS && matchC && matchK;
    });
  }, [products, filters]);

  // Agrega una categoría nueva al vuelo (config en datos: project_settings/productCategories).
  const handleAddCategory = async (name) => {
    const clean = (name || "").trim();
    if (!clean) return null;
    const id = clean.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || `cat-${Date.now()}`;
    if (categories.some((c) => c.id === id)) { alert("Ya existe una categoría con ese nombre."); return null; }
    const newCat = { id, name: clean, icon: "Package", pricingStrategy: "per_unit", attributes: [{ key: "detail", label: "Detalle", type: "text" }] };
    const next = [...categories, newCat];
    try {
      await setDoc(doc(db, "project_settings", "productCategories"), { categories: next }, { merge: true });
      setCategories(next);
      return newCat;
    } catch (e) {
      console.error("No se pudo guardar la categoría:", e);
      alert("No se pudo guardar la categoría: " + e.message);
      return null;
    }
  };

  const handleSave = async (data) => {
    // El ProductModal ya subió imagen/dieline y trae las URLs en `data`.
    if (selected) await updateProduct(db, selected.id, { ...data, _bumpVersion: true });
    else await createProduct(db, data, user);
    // Auto-registra en el catálogo los componentes del BOM que aún no existan (kind=componente),
    // para que aparezcan en la lista de componentes y sean reutilizables. Hereda cliente y
    // categoría del producto (editables luego); usa la foto pegada de la línea.
    if (data.kind === "producto" && Array.isArray(data.bom) && data.bom.length) {
      const existing = new Set(
        products.filter((p) => p.kind === "componente").map((p) => String(p.code || p.name || "").toLowerCase().trim())
      );
      for (const line of data.bom) {
        const key = String(line.code || "").toLowerCase().trim();
        if (!key || existing.has(key)) continue;
        try {
          await createProduct(db, {
            kind: "componente",
            name: line.code,
            code: line.code,
            categoryId: data.categoryId || "otro",
            clientId: data.clientId || null,
            imageUrl: line.previewUrl || "",
            feedsInventory: true,
          }, user);
          existing.add(key);
        } catch (e) {
          console.warn("No se pudo auto-crear el componente:", line.code, e);
        }
      }
    }
    setIsModalOpen(false);
    setSelected(null);
    fetchAll();
  };

  const handleDelete = async (id) => {
    if (window.confirm("¿Eliminar este producto del catálogo? No se puede deshacer.")) {
      await deleteDoc(doc(db, "products", id));
      fetchAll();
    }
  };

  return (
    <div className="p-4 md:p-6 h-full flex flex-col">
      {canManage && (
        <ProductModal
          isOpen={isModalOpen}
          onClose={() => { setIsModalOpen(false); setSelected(null); setViewMode(false); }}
          readOnly={viewMode}
          onSave={handleSave}
          productData={selected}
          categories={categories}
          clients={clients}
          materials={materials}
          storage={storage}
          onAddCategory={handleAddCategory}
          components={products.filter((p) => p.kind === "componente")}
          defaultKind={filters.kind === "componente" ? "componente" : "producto"}
        />
      )}

      <div className="flex-shrink-0">
        <div className="flex flex-col md:flex-row justify-between items-center mb-4 gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">Catálogo de Productos</h1>
            <p className="text-sm text-gray-500">SKUs reutilizables (cajas, displays, banners…) <b>y componentes</b> que los arman. Reorden rápido.</p>
          </div>
          {canManage && (
            <button onClick={() => { setSelected(null); setViewMode(false); setIsModalOpen(true); }} className="flex items-center gap-2 px-4 py-2 font-bold text-white bg-blue-600 rounded-lg hover:bg-blue-700 w-full md:w-auto justify-center" title="Producto o componente — eliges el tipo en el formulario">
              <IconPlus /> Nuevo
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4 p-4 bg-gray-50 rounded-lg">
          <input type="text" placeholder="Buscar por código, SKU o nombre…" className="md:col-span-2 w-full p-2 border border-gray-300 rounded-md" value={filters.search} onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))} />
          <select className="w-full p-2 border border-gray-300 rounded-md" value={filters.kind} onChange={(e) => setFilters((f) => ({ ...f, kind: e.target.value }))}>
            <option value="all">Productos y componentes</option>
            <option value="producto">Solo productos (SKU)</option>
            <option value="componente">Solo componentes</option>
          </select>
          <select className="w-full p-2 border border-gray-300 rounded-md" value={filters.category} onChange={(e) => setFilters((f) => ({ ...f, category: e.target.value }))}>
            <option value="all">Todas las categorías</option>
            {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
      </div>

      <div className="flex-grow bg-white rounded-xl shadow-lg overflow-hidden">
        {isLoading ? <div className="text-center p-10">Cargando…</div> : (
          <div className="overflow-auto h-full">
            <table className="w-full text-sm text-left text-gray-600">
              <thead className="text-xs text-gray-700 uppercase bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="px-6 py-3">Preview</th>
                  <th className="px-6 py-3">SKU</th>
                  <th className="px-6 py-3">Código</th>
                  <th className="px-6 py-3">Nombre</th>
                  <th className="px-6 py-3">Categoría</th>
                  <th className="px-6 py-3">Cliente</th>
                  <th className="px-6 py-3">Troquel</th>
                  {canManage && <th className="px-6 py-3 text-center">Acciones</th>}
                </tr>
              </thead>
              <tbody className="divide-y">
                {filtered.length === 0 && (
                  <tr><td colSpan={canManage ? 8 : 7} className="px-6 py-10 text-center text-gray-400">
                    Catálogo vacío. {canManage ? 'Crea tu primer producto con "Nuevo Producto".' : ""}
                  </td></tr>
                )}
                {filtered.map((p) => (
                  <tr key={p.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      {p.imageUrl
                        ? <img src={p.imageUrl} alt={p.name} onClick={() => setLightbox({ url: p.imageUrl, title: `${p.sku} · ${p.name}` })} title="Clic para ampliar" className="w-12 h-12 object-cover rounded-md cursor-zoom-in hover:ring-2 hover:ring-blue-500 transition" />
                        : <div className="w-12 h-12 rounded-md bg-gray-100 flex items-center justify-center text-gray-300 text-xs">—</div>}
                    </td>
                    <td className="px-6 py-4 font-mono text-xs">{p.sku}</td>
                    <td className="px-6 py-4 font-mono text-xs text-gray-700">{p.code || <span className="text-gray-300">—</span>}</td>
                    <td className="px-6 py-4 font-medium text-gray-900">
                      {p.kind === "componente"
                        ? <span className="mr-2 align-middle text-[10px] font-semibold px-1.5 py-0.5 rounded bg-fuchsia-100 text-fuchsia-700">COMP</span>
                        : (p.bom?.length > 0 && <span className="mr-2 align-middle text-[10px] font-semibold px-1.5 py-0.5 rounded bg-blue-100 text-blue-700" title={`${p.bom.length} componentes`}>BOM</span>)}
                      {p.name}{p.active === false && <span className="ml-2 text-xs text-gray-400">(inactivo)</span>}
                    </td>
                    <td className="px-6 py-4">{catName(p.categoryId)}</td>
                    <td className="px-6 py-4">{p.clientId ? clientName(p.clientId) : "Genérico"}</td>
                    <td className="px-6 py-4">{p.dieline?.fileUrl ? <a href={p.dieline.fileUrl} target="_blank" rel="noreferrer" className="text-blue-600 underline">ver</a> : "—"}</td>
                    {canManage && (
                      <td className="px-6 py-4">
                        <div className="flex justify-center items-center gap-3">
                          {p.kind === "componente" && (
                            <button onClick={() => downloadQrPng(productQrPayload(p), p.code || p.sku, `${p.code || p.sku}.png`)} title="Descargar QR (PNG) con identificación" className="text-gray-500 hover:text-emerald-600"><IconQr /></button>
                          )}
                          <button onClick={() => { setSelected(p); setViewMode(true); setIsModalOpen(true); }} title="Ver (solo lectura)" className="text-gray-500 hover:text-indigo-600"><IconEye /></button>
                          <button onClick={() => { setSelected(p); setViewMode(false); setIsModalOpen(true); }} title="Editar" className="text-gray-500 hover:text-blue-600"><IconPencil /></button>
                          <button onClick={() => handleDelete(p.id)} title="Eliminar" className="text-gray-500 hover:text-red-600"><IconTrash /></button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Lightbox: ver el 1UP / preview del producto en grande */}
      {lightbox && (
        <div className="fixed inset-0 z-[120] bg-black bg-opacity-80 flex items-center justify-center p-4" onClick={() => setLightbox(null)}>
          <button onClick={() => setLightbox(null)} className="absolute top-4 right-5 text-white text-4xl leading-none hover:text-gray-300" title="Cerrar (Esc)">×</button>
          <figure className="max-w-[90vw] max-h-[90vh] flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
            <img src={lightbox.url} alt={lightbox.title || "Preview"} className="max-w-[90vw] max-h-[82vh] object-contain rounded-lg shadow-2xl bg-white" />
            {lightbox.title && <figcaption className="mt-3 text-white text-sm">{lightbox.title}</figcaption>}
          </figure>
        </div>
      )}
    </div>
  );
};

export default ProductsView;
